import React from "react";
import { Group, Loader, Paper, Stack, Text } from "@mantine/core";
import type { AppointmentScheduledMessage } from "../../types/appointmentScheduledMessages";
import type { PreviewState } from "../../hooks/useAppointmentScheduledMessagePreviews";
import { TERMINAL_STATUSES } from "../../utils/appointmentScheduledMessageDisplay";
import classes from "./AppointmentMessagePreview.module.css";

interface AppointmentMessagePreviewProps {
  /** Preview lifecycle for this row (from useAppointmentScheduledMessagePreviews). */
  preview: PreviewState | undefined;
  /** The row's status — drives the terminal "won't reflect edits" note. */
  status: AppointmentScheduledMessage["status"];
}

/**
 * Renders the fully-rendered SMS body for an appointment scheduled message — the
 * exact text that would arrive on the client's phone. Shared by the Transaction
 * Modal's Scheduled Reminders panel and the two-way chat "Scheduled messages"
 * drawer so the preview look + copy can't drift. Shows a loading / error
 * fallback until the body resolves.
 */
export const AppointmentMessagePreview: React.FC<
  AppointmentMessagePreviewProps
> = ({ preview, status }) => {
  if (!preview || preview.status === "loading") {
    return (
      <Group gap="xs">
        <Loader size="xs" />
        <Text size="sm" c="dimmed">
          Loading message…
        </Text>
      </Group>
    );
  }
  if (preview.status === "error") {
    return (
      <Text size="sm" c="dimmed" fs="italic">
        Could not load message preview.
      </Text>
    );
  }
  const isTerminal = TERMINAL_STATUSES.has(status);
  return (
    <Stack gap={4}>
      <Paper p="xs" withBorder className={classes.box}>
        <Text size="sm" className={classes.body}>
          {preview.data.rendered_text ||
            "(empty body — template misconfigured)"}
        </Text>
      </Paper>
      {preview.data.forms_link_status === "pending_mint" && (
        <Text size="xs" c="dimmed" fs="italic">
          Forms link will be added at send time.
        </Text>
      )}
      {isTerminal && (
        // SENT/FAILED/CANCELLED/SKIPPED rows are a current re-render rather than
        // the historical bytes that hit the carrier.
        <Text size="xs" c="dimmed" fs="italic">
          Any changes to client's profile will not be reflected in sent
          messages.
        </Text>
      )}
    </Stack>
  );
};

export default AppointmentMessagePreview;
